import { useState } from "react";
import { UserPlus } from "lucide-react";
import Button from "../components/Button";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import Tag from "../components/Tag";
import TextField from "../components/TextField";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import { apiRequest } from "../api";
import useApiQuery from "../useApiQuery";
import formStyles from "./RewardDrawer.module.css";
import { TEAM_ACCESS } from "./teamData";
import styles from "./TeamPage.module.css";

const PAGE_SIZE = 11;

const COLUMNS = ["Name", "Access", "Email", "Action"];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Runs an API call for a modal form, keeping its pending state and error message
function useSubmit() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const run = async (request) => {
    setSubmitting(true);
    setError(null);
    try {
      return await request();
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, error, setError, run };
}

function AccessOptions({ value, onChange }) {
  return (
    <fieldset className={formStyles.reasons}>
      <legend className={styles.legend}>Access</legend>
      {Object.entries(TEAM_ACCESS).map(([key, access]) => (
        <label key={key} className={`${formStyles.reason} ${styles.option} d-flex`}>
          <input
            type="radio"
            name="team-access"
            className={formStyles.radio}
            checked={value === key}
            onChange={() => onChange(key)}
          />
          <span>
            {access.label}
            <span className={styles.optionHint}>{access.description}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function AddMemberModal({ onClose, onAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [access, setAccess] = useState("standard");
  const { submitting, error, setError, run } = useSubmit();

  const submit = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    const result = await run(() =>
      apiRequest("/api/admin/team", { method: "POST", body: { name: name.trim(), email: trimmedEmail, access } })
    );
    if (result) {
      onAdded();
      onClose();
    }
  };

  return (
    <Modal open title="Add member" subtitle="They'll get an email link to set their password." onClose={onClose}>
      <form className={formStyles.form} onSubmit={submit} noValidate>
        <TextField label="Full name" value={name} autoFocus maxLength={60} onChange={(event) => setName(event.target.value)} />
        <TextField
          label="Work email"
          type="email"
          value={email}
          aria-invalid={Boolean(error)}
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
        />
        <AccessOptions value={access} onChange={setAccess} />
        {error ? <p className={formStyles.feedbackInvalid}>{error}</p> : null}
        <div className={`${formStyles.ctas} d-flex`}>
          <Button type="button" variant="ghost" className={formStyles.cancel} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!name.trim() || !email.trim() || submitting}>
            {submitting ? "Sending…" : "Send invite"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ManageMemberModal({ member, onClose, onChanged }) {
  const [access, setAccess] = useState(member.access);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const { submitting, error, run } = useSubmit();

  const finish = (result) => {
    if (result) {
      onChanged();
      onClose();
    }
  };

  const save = async (event) => {
    event.preventDefault();
    finish(await run(() => apiRequest(`/api/admin/team/${member.id}`, { method: "PATCH", body: { access } })));
  };

  const remove = async () => {
    finish(await run(() => apiRequest(`/api/admin/team/${member.id}`, { method: "DELETE" })));
  };

  return (
    <Modal open title={member.name} subtitle={member.email} onClose={onClose}>
      {confirmingRemove ? (
        <div className={formStyles.form}>
          <p className={styles.confirm}>{member.name} will lose access to the admin portal straight away.</p>
          {error ? <p className={formStyles.feedbackInvalid}>{error}</p> : null}
          <div className={`${formStyles.ctas} d-flex`}>
            <Button type="button" variant="ghost" className={formStyles.cancel} onClick={() => setConfirmingRemove(false)}>
              Back
            </Button>
            <Button type="button" className={styles.danger} disabled={submitting} onClick={remove}>
              {submitting ? "Removing…" : "Remove member"}
            </Button>
          </div>
        </div>
      ) : (
        <form className={formStyles.form} onSubmit={save}>
          <AccessOptions value={access} onChange={setAccess} />
          {error ? <p className={formStyles.feedbackInvalid}>{error}</p> : null}
          <div className={`${formStyles.ctas} d-flex`}>
            <Button type="button" variant="ghost" className={formStyles.cancel} onClick={() => setConfirmingRemove(true)}>
              Remove
            </Button>
            <Button type="submit" disabled={access === member.access || submitting}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default function TeamPage({ user }) {
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [managed, setManaged] = useState(null);
  const { data, status, reload } = useApiQuery("/api/admin/team");

  const members = data?.members ?? [];
  const canManage = Boolean(data?.canManage);
  const pageCount = Math.max(1, Math.ceil(members.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = members.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section className={styles.page}>
      <header className={`${styles.pageHeader} d-flex align-items-center justify-content-between`}>
        <h1 className={styles.title}>Team</h1>
        {canManage ? (
          <Button size="sm" className={styles.addButton} onClick={() => setAdding(true)}>
            <UserPlus size={20} strokeWidth={1.75} aria-hidden="true" />
            Add member
          </Button>
        ) : null}
      </header>

      <div className={tableStyles.wrap}>
        <table className={`${tableStyles.table} ${tableStyles.fixed} ${styles.table}`}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((member) => {
              const isSelf = String(member.id) === String(user?.id);

              return (
                <tr key={member.id}>
                  <td className={styles.primary}>
                    <span className="d-inline-flex align-items-center gap-2">
                      {member.name}
                      {member.pending ? <Tag tone="warning">Invited</Tag> : null}
                      {member.status !== "active" ? <Tag tone="danger">{member.status}</Tag> : null}
                    </span>
                  </td>
                  <td className={styles.primary}>{TEAM_ACCESS[member.access]?.label}</td>
                  <td className={styles.primary}>{member.email}</td>
                  <td>
                    {isSelf ? (
                      <span className={styles.self}>You</span>
                    ) : canManage ? (
                      <TextLink arrow={false} className={styles.manage} onClick={() => setManaged(member)}>
                        Manage
                      </TextLink>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {status === "loading" && !data ? (
              <tr>
                <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                  Loading the team…
                </td>
              </tr>
            ) : status === "error" ? (
              <tr>
                <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                  We couldn't load the team.{" "}
                  <TextLink arrow={false} onClick={reload}>
                    Try again
                  </TextLink>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />

      {adding ? <AddMemberModal onClose={() => setAdding(false)} onAdded={reload} /> : null}
      {managed ? <ManageMemberModal member={managed} onClose={() => setManaged(null)} onChanged={reload} /> : null}
    </section>
  );
}
